import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { formatIndianDateTime } from '../../utils/formatters';
import { FileText, Download } from 'lucide-react';

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get('/admin/audit-logs');
      setLogs(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setLogs([]);
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csv = [
      ['Timestamp', 'Admin', 'Action', 'Resource Type', 'Resource ID'].join(','),
      ...logs.map(log => [
        new Date(log.createdAt).toLocaleString(),
        log.adminName,
        log.action,
        log.resourceType,
        log.resourceId
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-600 mt-1">{logs.length} recorded actions</p>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold px-6 py-3 rounded-lg"
          >
            <Download className="w-5 h-5" />
            Export CSV
          </button>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Admin</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Resource</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatIndianDateTime(log.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {log.adminName}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-primary-light text-primary text-xs font-semibold rounded">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.resourceType}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-gray-600">
                        {JSON.stringify(log.changes)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-600">No audit logs yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminAuditLogs;